import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  ImageList,
  ImageListItem,
  Modal,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import { Close, ChevronLeft, ChevronRight } from "@mui/icons-material";
import ScreenshotImage from "./ScreenshotImage";

const screenshots = [
  {
    src: "/screenshots/dashboard.png",
    title: "Dashboard Principal",
    description: "Visão geral com métricas e indicadores estratégicos",
  },
  {
    src: "/screenshots/thinkboard.png",
    title: "ThinkBoard",
    description: "Kanban para gestão visual de tarefas",
  },
  {
    src: "/screenshots/insights.png",
    title: "Insights IA",
    description: "Recomendações estratégicas geradas por IA",
  },
  {
    src: "/screenshots/mapa-estrategico.png",
    title: "Mapa Estratégico",
    description: "Visualização completa da estratégia",
  },
  {
    src: "/screenshots/metas.png",
    title: "Metas",
    description: "Gestão de Metas e Indicadores",
  },
  {
    src: "/screenshots/swot.png",
    title: "Análise SWOT",
    description: "Forças, Fraquezas, Oportunidades e Ameaças",
  },
];

const MotionBox = motion(Box);

const ScreenshotsSection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  const handleImageClick = (index: number) => {
    setSelectedImage(index);
  };

  const handleClose = () => {
    setSelectedImage(null);
  };

  const handlePrev = () => {
    if (selectedImage !== null) {
      setSelectedImage(
        selectedImage === 0 ? screenshots.length - 1 : selectedImage - 1
      );
    }
  };

  const handleNext = () => {
    if (selectedImage !== null) {
      setSelectedImage(
        selectedImage === screenshots.length - 1 ? 0 : selectedImage + 1
      );
    }
  };

  const getCols = () => {
    if (isMobile) return 1;
    if (isTablet) return 2;
    return 3;
  };

  return (
    <Box
      id="screenshots"
      sx={{
        py: { xs: 10, md: 14 },
        backgroundColor: "white",
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: "#042E4B",
                mb: 2,
                fontSize: { xs: "2rem", md: "2.75rem" },
              }}
            >
              Veja o ThinkWay{" "}
              <Box component="span" sx={{ color: "#EF443C" }}>
                em Ação
              </Box>
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#64748B",
                fontWeight: 400,
                maxWidth: 600,
                mx: "auto",
              }}
            >
              Explore as principais telas da plataforma e descubra como
              simplificamos o planejamento estratégico.
            </Typography>
          </motion.div>
        </Box>

        {/* Screenshots Gallery */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <ImageList cols={getCols()} gap={24} sx={{ overflow: "visible" }}>
            {screenshots.map((screenshot) => (
              <ImageListItem
                key={screenshot.title}
                sx={{
                  cursor: "pointer",
                  borderRadius: 3,
                  overflow: "hidden",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 16px 40px rgba(0,0,0,0.15)",
                  },
                }}
                onClick={() => handleImageClick(screenshots.indexOf(screenshot))}
              >
                <Box
                  sx={{
                    position: "relative",
                    minHeight: 200,
                  }}
                >
                  <ScreenshotImage
                    src={screenshot.src}
                    alt={screenshot.title}
                    placeholderText={screenshot.title}
                    minHeight={200}
                    borderRadius={0}
                    objectFit="cover"
                  />

                  {/* Overlay on hover */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background:
                        "linear-gradient(transparent, rgba(4,46,75,0.9))",
                      p: 2,
                      transform: "translateY(100%)",
                      transition: "transform 0.3s ease",
                      ".MuiImageListItem-root:hover &": {
                        transform: "translateY(0)",
                      },
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ color: "white", fontWeight: 600 }}
                    >
                      {screenshot.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.8)" }}
                    >
                      {screenshot.description}
                    </Typography>
                  </Box>
                </Box>
              </ImageListItem>
            ))}
          </ImageList>
        </motion.div>
      </Container>

      {/* Lightbox Modal */}
      <Modal
        open={selectedImage !== null}
        onClose={handleClose}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <MotionBox
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          sx={{
            position: "relative",
            maxWidth: "90vw",
            maxHeight: "90vh",
            outline: "none",
          }}
        >
          {selectedImage !== null && (
            <>
              <IconButton
                onClick={handleClose}
                sx={{
                  position: "absolute",
                  top: -48,
                  right: 0,
                  color: "white",
                  backgroundColor: "rgba(0,0,0,0.5)",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.7)",
                  },
                }}
              >
                <Close />
              </IconButton>

              <IconButton
                onClick={handlePrev}
                sx={{
                  position: "absolute",
                  left: -60,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "white",
                  backgroundColor: "rgba(0,0,0,0.5)",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.7)",
                  },
                  display: { xs: "none", md: "flex" },
                }}
              >
                <ChevronLeft />
              </IconButton>

              <IconButton
                onClick={handleNext}
                sx={{
                  position: "absolute",
                  right: -60,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "white",
                  backgroundColor: "rgba(0,0,0,0.5)",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.7)",
                  },
                  display: { xs: "none", md: "flex" },
                }}
              >
                <ChevronRight />
              </IconButton>

              <ScreenshotImage
                src={screenshots[selectedImage].src}
                alt={screenshots[selectedImage].title}
                placeholderText={screenshots[selectedImage].title}
                minHeight={{ xs: 300, md: 400 }}
                objectFit="contain"
                borderRadius={2}
                containerSx={{
                  minWidth: { xs: "80vw", md: 600 },
                }}
                imageSx={{
                  maxWidth: "100%",
                  maxHeight: "85vh",
                }}
              />

              <Box
                sx={{
                  textAlign: "center",
                  mt: 2,
                  color: "white",
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  {screenshots[selectedImage].title}
                </Typography>
                <Typography variant="body2" color="rgba(255,255,255,0.7)">
                  {screenshots[selectedImage].description}
                </Typography>
              </Box>
            </>
          )}
        </MotionBox>
      </Modal>
    </Box>
  );
};

export default ScreenshotsSection;
